import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../../api";
import { Sec } from "./parts";
import ClientBriefReview from "./ClientBriefReview";
import { briefLine } from "./briefModel.mjs";

/*
 * THE CLIENT BRIEF on the client card: where it stands, and the way to it.
 *
 * Asked for on 2026-10-15: a brief written when a lead becomes a client, from the
 * calls and emails. It lives in Slack as the "Initial Client Brief" canvas in the
 * client's internal channel (src/brief). The server writes a new client's brief
 * on its own but never posts it ("only pushed from the CRM, not automatic"):
 * Review and post to Slack opens the brief as it will read there
 * (ClientBriefReview.js), and the post is a button at its foot ("I'd want to be
 * able to review it before sending"). This line also links to the posted canvas,
 * writes one for an older client, and rewrites one after the kickoff (a posted
 * canvas is replaced in place).
 *
 * Drawn in the lead block's own grammar (parts.js): a small label, one line, and
 * links rather than buttons, like the agreement link on New client. Undrawn by
 * Gabriel; its place and look should follow his mockup when there is one.
 *
 * Writing takes minutes, so while it runs the line polls every POLL_MS.
 *
 * Props:
 *   companyId   the company the card is for
 */
const POLL_MS = 8000;

const LINK_WORDS = { write: "Write client brief", rewrite: "Rewrite", retry: "Try again" };

export default function ClientBrief({ companyId }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      setStatus(await api(`/companies/${companyId}/brief`));
    } catch (e) {
      setError(e.message);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const line = briefLine(status);

  useEffect(() => {
    if (!line.polling) return undefined;
    timer.current = setTimeout(load, POLL_MS);
    return () => clearTimeout(timer.current);
  }, [line.polling, status, load]);

  async function write() {
    setBusy(true);
    setError(null);
    try {
      setStatus(await api(`/companies/${companyId}/brief`, { method: "POST" }));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (!status && !error) return null;
  return (
    <Sec className="rec-lead__sec--12">
      <span className="rec-lead__small">Initial Client Brief</span>
      <span className="rec-sub">{error || line.text}</span>
      {line.actions.length > 0 && (
        <div className="rec-lead__links">
          {line.actions.map((key) => {
            if (key === "open") {
              return (
                <a key={key} href={line.url} target="_blank" rel="noopener noreferrer">
                  Open in Slack
                </a>
              );
            }
            if (key === "review") {
              // Gabriel asked for this one yellow and prominent (2026-10-15), so
              // it is a button where the rest of the line is links.
              return (
                <button key={key} type="button" className="rec-btn rec-btn--primary" disabled={busy} onClick={() => setReviewing(true)}>
                  Review and post to Slack
                </button>
              );
            }
            if (key === "view") {
              return (
                <button key={key} type="button" className="rec-link" onClick={() => setReviewing(true)}>
                  Review
                </button>
              );
            }
            return (
              <button key={key} type="button" className="rec-link" disabled={busy} onClick={write}>
                {LINK_WORDS[key]}
              </button>
            );
          })}
        </div>
      )}
      {reviewing && (
        <ClientBriefReview
          companyId={companyId}
          onClose={() => setReviewing(false)}
          onPosted={(next) => setStatus(next)}
        />
      )}
    </Sec>
  );
}
